import React, { useReducer } from 'react';
import _ from 'lodash';
import {
  EVENT_TYPES_DEFAULT,
  filterEvents,
  getResources,
  RESOURCE_TYPES_DEFAULT,
  EVENT_TYPES,
  CALENDAR_TYPES,
  CALENDAR_TYPES_DEFAULT,
  eventsResourceMapping,
} from '../Utils/helper/schedulerHelper';
import {
  getAcademicPlannerSession,
  getCourseVersion,
  getReferenceApi,
  getSessionData,
} from '../Utils/apiServices';

export const initialState = {
  academicsData: [],
  events: [],
  allEvents: [],
  ...getResources(RESOURCE_TYPES_DEFAULT, { facilities: [], faculties: [] }),
  // resources: [],
  courseVersionData: null, //Subjects
  term: '',
  resourceType: RESOURCE_TYPES_DEFAULT,
  eventType: EVENT_TYPES_DEFAULT,
  faculties: [],
  facilities: [],
  activityTypes: [],
  activities: [],
  referenceActivities: [],
  // resourceColumns: [],
  selectedCourseVersions: [],
  selectedReferece: {},
  isEventEdit: false,
  isEventView: false,
  selectedEvent: null,
  selectedDetailedResource: null,
  calendarType: CALENDAR_TYPES_DEFAULT,
  sessions: [],
};

export const ACTIONS = {
  UPDATE: 'update',
  UPDATE_PROGRAM: 'updateProgram',
  UPDATE_YEAR: 'updateYear',
  UPDATE_INTAKE: 'updateYear',
  UPDATE_TERM: 'updateTerm',
  UPDATE_RESOURCE_TYPE: 'updateResourceType',
  UPDATE_EVENT_TYPE: 'updateEventType',
  UPDATE_FACULTY_FACILITY_ACTIVITIES: 'updateFacultyFacilityActivity',
  UPDATE_SELECTED_VERSIONS: 'updateSelectedVersions',
  UPDATE_ACTIVITIES: 'updateactivities',
  UPDATE_REFERENCE_ACTIVITIES: 'updateReferenceActivities',
  UPDATE_SESSIONS: 'updateSessions',
  UPDATE_COURSE_VERSION_DATA: 'updateCourseVersionData',
  UPDATE_SELECTED_SUBJECTS: 'updateSelectedSubjects',
  SET_SELECTED_EVENT: 'setSelectedEvent',
  CLEAR_SELECTED_EVENT: 'clearSelectedEvent',
  SET_EVENT_EDIT_FLAG: 'setEventEditFlag',
  SET_EVENT_VIEW_FLAG: 'setEventViewFlag',
  SET_DETAILED_RESOURCE: 'setDetailedResource',
  CLEAR_DETAILED_RESOURCE: 'clearDetailedResource',
  SET_CALENDAR_TYPE: 'setCalendarType',
};

const getFilterOptionsFromState = function (state) {
  return _.pick(state, [
    'allEvents',
    'selectedSubjects',
    'resourceType',
    'eventType',
  ]);
};

export const reducer = (state, action) => {
  const { type, value } = action;
  switch (type) {
    case ACTIONS.UPDATE:
      return {
        ...state,
        ...value,
      };

    case ACTIONS.UPDATE_ACADEMIC_DATA:
      return {
        ...state,
        academicsData: value,
      };

    case ACTIONS.UPDATE_PROGRAM:
      // clear all dependent fields and other
      return {
        ...state,
        program: value,
        events: [],
        activities: [],
        referenceActivities: [],
        sessions: [],
        year: '',
        intake: '',
        term: '',
        courseVersionData: [],
      };

    case ACTIONS.UPDATE_YEAR:
      return {
        ...state,
        year: value,
      };
    case ACTIONS.UPDATE_INTAKE:
      return {
        ...state,
        intake: value,
      };

    case ACTIONS.UPDATE_TERM:
      return {
        ...state,
        term: value,
        events: [],
        activities: [],
        referenceActivities: [],
        sessions: [],
      };

    case ACTIONS.UPDATE_FACULTY_FACILITY_ACTIVITIES:
      const { Activity = [], Facility = {}, Faculty = {} } = value || {};
      return {
        ...state,
        activityTypes: Activity,
        // facilities: _(Facility).values()?.map(f => ({ ...f, title: f.facilityName })).value(),
        // faculties: _(Faculty).values().map(f => ({ ...f, title: f.facilityName })).value()
        facilities: _.values(Facility),
        faculties: _.values(Faculty),
        ...getResources(state.resourceType, {
          facilities: _.values(Facility),
          faculties: _.values(Faculty),
        }),
      };

    case ACTIONS.UPDATE_EVENT_TYPE:
      return {
        ...state,
        eventType: value,
        events: filterEvents({
          ...getFilterOptionsFromState(state),
          eventType: value,
        }),
      };

    case ACTIONS.UPDATE_RESOURCE_TYPE:
      // update resources and events change resourceId
      const resourceType = value;

      return {
        ...state,
        resourceType,
        events: eventsResourceMapping(state.events, value),
        ...getResources(resourceType, state),
      };

    case ACTIONS.UPDATE_ACTIVITIES: {
      // Removing startTime and endTime as these create a recurring Event in calendar
      const activities = value?.map((s) =>
        _.omit(
          {
            ...s,
            eventType: EVENT_TYPES.ACTIVITY,
            title: `${s.activity} ${s?.SubjectPlanner?.courseName || ''}`,
            start: `${s.startDate}T${s.startTime}`,
            end: `${s.endDate}T${s.endTime}`,
          },
          ['startTime', 'endTime']
        )
      );

      const allEvents = [
        ...state.allEvents.filter((e) => e.eventType !== EVENT_TYPES.ACTIVITY),
        ...activities,
      ];
      return {
        ...state,
        activities: activities,
        events: filterEvents({
          ...getFilterOptionsFromState(state),
          allEvents,
        }),
        allEvents,
      };
    }

    case ACTIONS.UPDATE_REFERENCE_ACTIVITIES: {
      const referenceActivities = value?.map((s) => ({
        ...s,
        eventType: EVENT_TYPES.REFERENCE,
        title: `${s.activityType} ${s.subjectCourseName || ''}`,
        start: `${s.startDate}`,
        end: `${s.endDate}`,
        allDay: true,
      }));

      const allEvents = [
        ...state.allEvents.filter((e) => e.eventType !== EVENT_TYPES.REFERENCE),
        ...referenceActivities,
      ];
      return {
        ...state,
        referenceActivities,
        events: filterEvents({
          ...getFilterOptionsFromState(state),
          allEvents,
        }),
        allEvents,
      };
    }

    case ACTIONS.UPDATE_SESSIONS: {
      const sessions = value?.map((s) => ({
        ...s,
        eventType: EVENT_TYPES.SESSION,
        title: `${s.activityType || ''} ${s.courseName || ''}`,
        start: `${s.startDate}`,
        end: `${s.endDate}`,
      }));

      const allEvents = [
        ...state.allEvents.filter((e) => e.eventType !== EVENT_TYPES.SESSION),
        ...sessions,
      ];

      return {
        ...state,
        sessions,
        events: filterEvents({
          ...getFilterOptionsFromState(state),
          allEvents,
        }),
        allEvents,
      };
    }

    case ACTIONS.UPDATE_COURSE_VERSION_DATA:
      return {
        ...state,
        courseVersionData: value || [],
        selectedCourseVersions: [],
      };

    case ACTIONS.UPDATE_SELECTED_COURSE_VERSIONS:
      return {
        ...state,
        selectedCourseVersions: value,
      };

    case ACTIONS.UPDATE_SELECTED_SUBJECTS:
      return {
        ...state,
        selectedSubjects: value,
        events: filterEvents({
          ...getFilterOptionsFromState(state),
          selectedSubjects: value,
        }),
      };

    case ACTIONS.SET_SELECTED_EVENT:
      return {
        ...state,
        selectedEvent: value,
      };

    case ACTIONS.CLEAR_SELECTED_EVENT:
      return {
        ...state,
        selectedEvent: null,
        isEventEdit: false,
        isEventView: false,
      };

    case ACTIONS.SET_EVENT_VIEW_FLAG:
      return {
        ...state,
        isEventView: value,
        isEventEdit: value ? false : state.isEventEdit,
      };
    case ACTIONS.SET_EVENT_EDIT_FLAG:
      return {
        ...state,
        isEventEdit: value,
        isEventView: value ? false : state.isEventView,
      };

    case ACTIONS.SET_DETAILED_RESOURCE:
      return {
        ...state,
        selectedDetailedResource: value,
      };

    case ACTIONS.CLEAR_DETAILED_RESOURCE:
      return {
        ...state,
        selectedDetailedResource: null,
      };

    case ACTIONS.SET_CALENDAR_TYPE:
      return {
        ...state,
        calendarType: value,
      };

    case 'reset':
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

export const SessionSchedulerContext = React.createContext();

export const SessionSchedulerContextProvider = ({
  initialValue = initialState,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialValue);

  const fetchDataByTerm = (
    termNew = state?.term,
    { hasToGetVersion = true }
  ) => {
    if (hasToGetVersion) {
      getCourseVersion(termNew)
        .then((res) => {
          if (res[termNew]) {
            dispatch({
              type: ACTIONS.UPDATE_COURSE_VERSION_DATA,
              value: res[termNew],
            });
          }
        })
        .catch((err) => {
          console.log('getCourseVersion: ', err);
        });
    }

    // call api to get data
    if (!termNew) {
      return;
    }
    
    if (state?.calendarType === CALENDAR_TYPES.ACADEMIC_PLANNER) {
      // Get Sessions by term and set events
        getReferenceApi(termNew)
      .then((res) => {
        if (res[termNew]) {
          dispatch({
            type: ACTIONS.UPDATE_REFERENCE_ACTIVITIES,
            value: res[termNew],
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
    }
    // getAcademicPlannerSession(termNew)
    //   .then((res) => {
    //     if (res[termNew]) {
    //       dispatch({
    //         type: ACTIONS.UPDATE_ACTIVITIES,
    //         // FIXME: remove hard coded value
    //         value: res[termNew],
    //       });
    //     }
    //   })
    //   .catch((err) => {
    //     console.error('SessionScheduler: getAcademicPlannerSession: ', err);
    //   });

    getSessionData({ term: termNew })
        .then((res) => {
          if (res['success'] === 'true' && res['data']) {
            dispatch({ type: ACTIONS.UPDATE_SESSIONS, value: res['data'] });
          }
        })
        .catch((err) => {
          console.log(err);
        });
  };

  return (
    <SessionSchedulerContext.Provider
      value={{
        state,
        dispatch,
        fetchDataByTerm,
      }}
      displayName="Session Scheduler Context"
    >
      {children}
    </SessionSchedulerContext.Provider>
  );
};

export const useSessionSchedulerContext = () => {
  const context = React.useContext(SessionSchedulerContext);

  if (!context) {
    throw new Error('useSessionSchedulerContext within provider');
  }

  return context;
};
